import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo, faFileAudio, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Col, Row, Card, Form, Button, Alert, InputGroup, Modal, ProgressBar } from '@themesberg/react-bootstrap';
import { collection, query, where, getDocs, addDoc, doc, onSnapshot } from 'firebase/firestore';
import { uploadToR2, generateUUID } from '../../utils/r2Upload';
import { httpsCallable } from 'firebase/functions';
import axios from 'axios';
import { db, functions } from '../../config/firebase';

export default () => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [fakeAccounts, setFakeAccounts] = useState([]);
  const [filteredFakeAccounts, setFilteredFakeAccounts] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState("");
  const [showAccountModal, setShowAccountModal] = useState(false);

  const [filterType, setFilterType] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [mediaType, setMediaType] = useState("text");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Job tracking state
  const [activeJob, setActiveJob] = useState(null);
  const jobUnsubscribeRef = useRef(null);

  // Fake hesapları yükle - sadece isTestUser olanları çek
  useEffect(() => {
    const q = query(collection(db, 'users'), where('isTestUser', '==', true));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const accounts = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        accounts.push({
          id: doc.id,
          name: data.name || '',
          profileImageUrl: data.profileImageUrl || null
        });
      });

      accounts.sort((a, b) => a.name.localeCompare(b.name));
      setFakeAccounts(accounts);
      setFilteredFakeAccounts(accounts);
    }, (err) => {
      console.error("Error loading fake accounts:", err);
      setError("Fake hesaplar yüklenirken hata oluştu: " + err.message);
    });

    return () => {
      unsubscribe();
      // Cleanup job listener
      if (jobUnsubscribeRef.current) {
        jobUnsubscribeRef.current();
      }
    };
  }, []);

  // Hesap arama
  useEffect(() => {
    if (!accountSearchTerm.trim()) {
      setFilteredFakeAccounts(fakeAccounts);
      return;
    }

    const searchLower = accountSearchTerm.toLowerCase().trim();
    const filtered = fakeAccounts.filter(account =>
      account.name.toLowerCase().includes(searchLower)
    ).sort((a, b) => a.name.localeCompare(b.name));

    setFilteredFakeAccounts(filtered);
  }, [accountSearchTerm, fakeAccounts]);

  // Kişi sayısını hesapla (server-side Cloud Function ile)
  const calculateRecipientCount = async () => {
    if (!selectedAccount) {
      setError("Lütfen önce bir hesap seçin.");
      return;
    }

    setCalculating(true);
    setError(null);

    try {
      // Tüm hesaplamalar artık Firebase Cloud Functions üzerinden (calculateBulkRecipients) yapılıyor.
      const calculateFn = httpsCallable(functions, 'calculateBulkRecipients', { timeout: 180000 });
      const result = await calculateFn({
        senderAccountId: selectedAccount.id,
        filterType: filterType
      });

      if (result.data && result.data.success) {
        setRecipientCount(result.data.count);
      } else {
        throw new Error("Kişi sayısı hesaplanırken sunucu hatası oluştu.");
      }
    } catch (err) {
      console.error("Error calculating recipient count:", err);
      setError("Kişi sayısı hesaplanırken hata oluştu: " + err.message);
    } finally {
      setCalculating(false);
    }
  };

  // Medya seçimi
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setMediaType("image");
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAudioSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedAudio(file);
      setMediaType("audio");
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedVideo(file);
      setMediaType("video");
    }
  };

  // Mesaj gönder (Job oluştur)
  const handleSendMessages = async () => {
    if (!selectedAccount) {
      setError("Lütfen bir hesap seçin.");
      return;
    }

    if (!filterType) {
      setError("Lütfen bir filtre seçin.");
      return;
    }

    if (mediaType === "text" && !messageText.trim()) {
      setError("Lütfen bir mesaj yazın.");
      return;
    }

    if (mediaType === "image" && !selectedImage && !messageText.trim()) {
      setError("Lütfen bir resim seçin veya mesaj yazın.");
      return;
    }

    if (mediaType === "audio" && !selectedAudio && !messageText.trim()) {
      setError("Lütfen bir ses dosyası seçin veya mesaj yazın.");
      return;
    }

    if (mediaType === "video" && !selectedVideo && !messageText.trim()) {
      setError("Lütfen bir video dosyası seçin veya mesaj yazın.");
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(false);

    try {
      // Medya yükle (varsa)
      let mediaUrl = null;
      if (mediaType === "image" && selectedImage) {
        const r2Path = `bulk_messages/images/${generateUUID()}.jpg`;
        mediaUrl = await uploadToR2(selectedImage, r2Path);
      } else if (mediaType === "audio" && selectedAudio) {
        const r2Path = `bulk_messages/audio/${generateUUID()}.m4a`;
        mediaUrl = await uploadToR2(selectedAudio, r2Path);
      } else if (mediaType === "video" && selectedVideo) {
        const r2Path = `bulk_messages/video/${generateUUID()}.mp4`;
        mediaUrl = await uploadToR2(selectedVideo, r2Path);
      }

      // Firestore'a job dokümanı oluştur
      const jobData = {
        senderAccountId: selectedAccount.id,
        senderName: selectedAccount.name,
        filterType: filterType,
        messageText: messageText || "",
        mediaType: mediaType,
        mediaUrl: mediaUrl,
        status: "pending",
        progress: 0,
        successCount: 0,
        failCount: 0,
        totalRecipients: 0,
        createdAt: new Date()
      };

      const jobDocRef = await addDoc(collection(db, 'bulkMessageJobs'), jobData);

      console.log("Bulk message job created:", jobDocRef.id);

      // Job dokümanını dinle (progress tracking)
      setActiveJob({
        id: jobDocRef.id,
        ...jobData
      });

      // Önceki listener varsa temizle
      if (jobUnsubscribeRef.current) {
        jobUnsubscribeRef.current();
      }

      const unsubscribe = onSnapshot(doc(db, 'bulkMessageJobs', jobDocRef.id), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setActiveJob(prev => ({
            ...prev,
            ...data,
            id: jobDocRef.id
          }));

          if (data.status === "completed") {
            setSending(false);
            setSuccess(true);
            setMessageText("");
            setSelectedImage(null);
            setSelectedAudio(null);
            setSelectedVideo(null);
            setImagePreview(null);
            setMediaType("text");
            setRecipientCount(null);
            setFilterType(null);

            alert(`İşlem tamamlandı! 🎉\n\nBaşarılı: ${data.successCount}\nBaşarısız: ${data.failCount}\n\nTüm mesajlar arka planda gönderildi.`);

            // Listener'ı temizle
            if (jobUnsubscribeRef.current) {
              jobUnsubscribeRef.current();
              jobUnsubscribeRef.current = null;
            }
            // activeJob'u birkaç saniye sonra temizle
            setTimeout(() => setActiveJob(null), 3000);

          } else if (data.status === "failed") {
            setSending(false);
            setError("Toplu mesaj gönderimi başarısız oldu: " + (data.error || "Bilinmeyen hata"));

            if (jobUnsubscribeRef.current) {
              jobUnsubscribeRef.current();
              jobUnsubscribeRef.current = null;
            }
            setTimeout(() => setActiveJob(null), 5000);
          }
        }
      });

      jobUnsubscribeRef.current = unsubscribe;

    } catch (err) {
      console.error("Error creating bulk message job:", err);
      setError("Toplu mesaj isteği oluşturulurken hata oluştu: " + (err.message || err.toString()));
      setSending(false);
    }
  };

  return (
    <>
      <div className="py-4">
        <h4>Toplu Mesaj Gönder</h4>
        <p className="text-muted mb-0">Mesajlar / Toplu Mesaj Gönder</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
          Mesajlar başarıyla gönderildi!
        </Alert>
      )}

      {/* Active Job Progress */}
      {activeJob && activeJob.status !== "completed" && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <h5 className="mb-3">
              {activeJob.status === "pending" ? "⏳ İş kuyruğa alındı..." :
                activeJob.status === "running" ? "🚀 Mesajlar gönderiliyor..." :
                  activeJob.status === "failed" ? "❌ Hata oluştu" : ""}
            </h5>

            {activeJob.status === "running" && (
              <>
                <ProgressBar
                  now={activeJob.progress || 0}
                  label={`${activeJob.progress || 0}%`}
                  animated
                  striped
                  variant="success"
                  className="mb-3"
                  style={{ height: '25px' }}
                />
                <div className="d-flex justify-content-between text-muted">
                  <span>Gönderilen: <strong>{activeJob.successCount || 0}</strong></span>
                  <span>Toplam: <strong>{activeJob.totalRecipients || "hesaplanıyor..."}</strong></span>
                  <span>Başarısız: <strong>{activeJob.failCount || 0}</strong></span>
                </div>
              </>
            )}

            {activeJob.status === "pending" && (
              <div className="text-muted">
                Cloud Function tetiklenmeyi bekliyor... Birkaç saniye içinde başlayacak.
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="alert alert-info mb-4">
            <strong>Bilgi:</strong> Mesajlar arka planda Cloud Function tarafından gönderilir. Sayfa açık kaldığı sürece ilerlemeyi takip edebilirsiniz.
          </div>

          {/* Hesap Seçin */}
          <div className="mb-4">
            <Form.Label className="mb-2">
              <strong>Hesap Seçin</strong>
            </Form.Label>
            <div>
              {selectedAccount ? (
                <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                  {selectedAccount.profileImageUrl ? (
                    <img
                      src={selectedAccount.profileImageUrl}
                      alt={selectedAccount.name}
                      className="rounded-circle"
                      style={{ width: 40, height: 40, objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: '0.8rem', color: '#fff' }}>
                        {selectedAccount.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="flex-grow-1">{selectedAccount.name}</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setSelectedAccount(null);
                      setFilterType(null);
                      setRecipientCount(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>
              ) : (
                <Button variant="outline-primary" onClick={() => setShowAccountModal(true)}>
                  Hesap Seçin
                </Button>
              )}
            </div>
          </div>

          {/* Filtre Butonları */}
          {selectedAccount && (
            <div className="mb-4">
              <Form.Label className="mb-2">
                <strong>Gönderilecek Kişileri Seçin</strong>
              </Form.Label>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  variant={filterType === "everyone" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setFilterType("everyone");
                    setRecipientCount(null);
                  }}
                >
                  Herkese Gönder
                </Button>
                <Button
                  variant={filterType === "vip" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setFilterType("vip");
                    setRecipientCount(null);
                  }}
                >
                  Şu anda vip olanlar
                </Button>
                <Button
                  variant={filterType === "neverSpoken" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setFilterType("neverSpoken");
                    setRecipientCount(null);
                  }}
                >
                  Daha önce konuşmadıkları
                </Button>
                <Button
                  variant={filterType === "purchased" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setFilterType("purchased");
                    setRecipientCount(null);
                  }}
                >
                  Satın Alım Yapanlar
                </Button>
                <Button
                  variant={filterType === "lastMonth" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setFilterType("lastMonth");
                    setRecipientCount(null);
                  }}
                >
                  Son 1 Aylık Üyeler
                </Button>
                <Button
                  variant={filterType === "favorites" ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setFilterType("favorites");
                    setRecipientCount(null);
                  }}
                >
                  Favorilerdeki Kullanıcılar
                </Button>
              </div>

              {filterType && (
                <div className="mb-3">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={calculateRecipientCount}
                    disabled={calculating}
                  >
                    {calculating ? "Hesaplanıyor..." : "Kişi sayısını gör"}
                  </Button>
                  {recipientCount !== null && (
                    <div className="mt-2">
                      <strong>Mesaj gönderilecek kişi sayısı: {recipientCount}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mesaj Yazma */}
          <div className="mb-4">
            <Form.Label className="mb-2">
              <strong>Gönderilecek Mesaj</strong>
            </Form.Label>

            {/* Medya Tipi Seçimi */}
            <div className="d-flex gap-2 mb-3">
              <Button
                variant={mediaType === "text" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => {
                  setMediaType("text");
                  setSelectedImage(null);
                  setSelectedAudio(null);
                  setSelectedVideo(null);
                  setImagePreview(null);
                }}
              >
                Metin
              </Button>
              <Button
                variant={mediaType === "image" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setMediaType("image")}
              >
                <FontAwesomeIcon icon={faImage} className="me-1" />
                Resim
              </Button>
              <Button
                variant={mediaType === "audio" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setMediaType("audio")}
              >
                <FontAwesomeIcon icon={faFileAudio} className="me-1" />
                Ses
              </Button>
              <Button
                variant={mediaType === "video" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setMediaType("video")}
              >
                <FontAwesomeIcon icon={faVideo} className="me-1" />
                Video
              </Button>
            </div>

            {/* Resim Seçimi ve Önizleme */}
            {mediaType === "image" && (
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                />
                {imagePreview && (
                  <div className="mt-3">
                    <div className="position-relative d-inline-block">
                      <img
                        src={imagePreview}
                        alt="Önizleme"
                        style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px' }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        onClick={() => {
                          setSelectedImage(null);
                          setImagePreview(null);
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                    <div className="mt-2">
                      <strong>Yüklenen resim</strong>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Ses Seçimi */}
            {mediaType === "audio" && (
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioSelect}
                />
                {selectedAudio && (
                  <div className="mt-2">
                    <strong>Seçilen dosya:</strong> {selectedAudio.name}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setSelectedAudio(null)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Video Seçimi */}
            {mediaType === "video" && (
              <div className="mb-3">
                <Form.Control
                  type="file"
                  accept="video/*"
                  onChange={handleVideoSelect}
                />
                {selectedVideo && (
                  <div className="mt-2">
                    <strong>Seçilen dosya:</strong> {selectedVideo.name}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setSelectedVideo(null)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Metin Mesajı */}
            <div className="mb-2">
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => setMessageText(prev => prev + " {{uye_sehir_da}} {{first_uye_name}}")}
                title="Kullanıcının adı ve şehrine göre akıllı etiket ekler"
              >
                Akıllı Mesaj (Ad - Şehir) Ekle
              </Button>
              <small className="ms-2 text-muted">Örn: İstanbul'da yaşıyorsun, Merhaba Alican Bey</small>
            </div>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder={mediaType === "text" ? "Mesaj yazın..." : "Mesaj yazın (opsiyonel)..."}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
            />
          </div>

          {/* Gönder Butonu */}
          <div className="mb-4">
            <Button
              variant="primary"
              size="lg"
              onClick={handleSendMessages}
              disabled={sending || !selectedAccount || !filterType || recipientCount === null}
            >
              {sending ? "İşlem devam ediyor..." : "Mesajları Gönder"}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Hesap Seçim Modal */}
      <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Hesap Seçin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="İsim ile ara..."
              value={accountSearchTerm}
              onChange={(e) => setAccountSearchTerm(e.target.value)}
            />
          </InputGroup>
          <div className="d-flex flex-wrap gap-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredFakeAccounts.map((account) => (
              <div
                key={account.id}
                className="d-flex flex-column align-items-center p-2 border rounded"
                style={{ cursor: 'pointer', width: '120px' }}
                onClick={() => {
                  setSelectedAccount(account);
                  setShowAccountModal(false);
                  setAccountSearchTerm("");
                }}
              >
                {account.profileImageUrl ? (
                  <img
                    src={account.profileImageUrl}
                    alt={account.name}
                    className="rounded-circle mb-2"
                    style={{ width: 49, height: 49, objectFit: 'cover' }}
                  />
                ) : (
                  <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mb-2" style={{ width: 49, height: 49 }}>
                    <span style={{ fontSize: '0.9rem', color: '#fff' }}>
                      {account.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-center small">{account.name}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
